import { FieldValues, Control, Path } from 'react-hook-form';
import * as React from 'react';

import {
  MultiSelectorBadgeValueProps,
  MultiSelectorSelectAllItem,
  MultiSelectorBadgeValue,
  MultiSelectorBadgeItem,
  MultiSelectorSeparator,
  MultiSelectorContent,
  MultiSelectorTrigger,
  MultiSelectorValue,
  MultiSelectorInput,
  MultiSelectorItem,
  MultiSelectorList,
  MultiSelector,
} from '../multiselector/multiselector';
import { FormControl, FormMessage, FormField, FormItem } from './form';
import { BadgeColor } from '../badge/badge';
import { cn } from '../../utils';

export interface FormMultiSelectorProps<T extends FieldValues = FieldValues> {
  componentProps?: {
    badgeValue?: MultiSelectorBadgeValueProps | boolean;
    contentSearch?: { placeholder: string } | boolean;
    emptyContent?: React.ReactNode;
    selectAllLabel?: string;
    badgeItems?: boolean;
    hasShadow?: boolean;
    label?: string;
  } & Partial<React.ComponentProps<typeof MultiSelector>>;
  options: { variant?: BadgeColor | string; disabled?: boolean; value: string; label: string }[];
  sortFunction?: (value: string[]) => string[];
  size?: 'medium' | 'small' | 'large';
  hideErrorMessage?: boolean;
  children?: React.ReactNode;
  customTrigger?: boolean;
  formControl: Control<T>;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  floating?: boolean;
  required?: boolean;
  name: Path<T>;
}

export function FormMultiSelector<T extends FieldValues>({
  sortFunction = (values) => values,
  hideErrorMessage,
  componentProps,
  customTrigger,
  placeholder,
  formControl,
  className,
  required,
  children,
  floating,
  disabled,
  options,
  size,
  name,
}: FormMultiSelectorProps<T>) {
  const {
    contentSearch = false,
    badgeValue = false,
    badgeItems = false,
    hasShadow = false,
    selectAllLabel,
    emptyContent,
    label,
    ...multiSelectorProps
  } = componentProps || {};

  // use as function not as component! If used as component functionalities from CustomTrigger are not applied
  const trigger = React.useCallback(() => {
    if (customTrigger) return children;

    if (badgeValue)
      return (
        <MultiSelectorBadgeValue
          placeholder={placeholder}
          {...(typeof badgeValue === 'object' ? badgeValue : {})}
        />
      );

    return <MultiSelectorValue placeholder={placeholder}>{label}</MultiSelectorValue>;
  }, [children, badgeValue, placeholder, label, customTrigger]);

  return (
    <FormField
      render={({ field, fieldState }) => (
        <FormItem className={cn('relative', className)}>
          <FormControl>
            <>
              <MultiSelector
                onValuesChange={(values) => field.onChange(sortFunction(values))}
                hasError={!!fieldState.error}
                values={field.value}
                disabled={disabled}
                required={required}
                floating={floating}
                {...multiSelectorProps}
              >
                <>
                  <MultiSelectorTrigger asChild={customTrigger} hasShadow={hasShadow} size={size}>
                    {trigger()}
                  </MultiSelectorTrigger>
                  <MultiSelectorContent>
                    {contentSearch && (
                      <>
                        <MultiSelectorInput
                          placeholder={
                            typeof contentSearch === 'object' ? contentSearch.placeholder : ''
                          }
                        />
                        <MultiSelectorSeparator />
                      </>
                    )}
                    {selectAllLabel && <MultiSelectorSelectAllItem label={selectAllLabel} />}
                    <MultiSelectorList
                      variant={badgeItems ? 'wrapped' : 'default'}
                      emptyContent={emptyContent}
                    >
                      {options.map((option) =>
                        badgeItems ? (
                          <MultiSelectorBadgeItem
                            variant={option.variant as BadgeColor}
                            disabled={option.disabled}
                            value={option.value!}
                            label={option.label}
                            key={option.value}
                          />
                        ) : (
                          <MultiSelectorItem
                            value={option.value!}
                            label={option.label}
                            key={option.value}
                          />
                        ),
                      )}
                    </MultiSelectorList>
                  </MultiSelectorContent>
                </>
              </MultiSelector>
            </>
          </FormControl>
          {!hideErrorMessage && <FormMessage />}
        </FormItem>
      )}
      control={formControl}
      name={name}
    />
  );
}
